import type { ReactNode } from 'react';
import { DoubleArrowPrev } from '@ui/icons';
import { cn } from '@shared/lib';

export interface PageCardProps {
  /** Page title shown in the gradient band. */
  title: ReactNode;
  /** Back-button label (pass the i18n string). */
  backLabel: string;
  onBack: () => void;
  /** Card body (rendered under the band). */
  children: ReactNode;
  className?: string;
}

/**
 * Internal-page surface (Figma): a full-width white card on the grey canvas with a gradient header
 * band ("‹‹ Back" + title) at the top. Shared by all the secondary pages (needs-attention, pending
 * settlements, recent activity, audit trail, help). Pair with route `staticData.hideRail`.
 */
export function PageCard({ title, backLabel, onBack, children, className }: PageCardProps) {
  return (
    <div className="p-5 pt-0">
      <div
        className={cn(
          'overflow-hidden rounded-lg border border-white bg-surface-raised',
          className,
        )}
      >
        <div className="flex h-12.5 items-center gap-6 bg-(image:--gradient-group-header) px-5">
          <button
            type="button"
            onClick={onBack}
            className="flex cursor-pointer items-center gap-1 rounded-full bg-surface-raised px-2.5 py-1.5 text-xs text-fg shadow-card hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            <DoubleArrowPrev className="size-4" aria-hidden />
            {backLabel}
          </button>
          <h1 className="text-lg font-semibold text-fg">{title}</h1>
        </div>
        {children}
      </div>
    </div>
  );
}
